import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { OptimalWallet } from 'src/app/interface/OptimalWallet';
import { BrokerAdapter } from './broker-adapter.interface';
import { IbrkBrokerAdapterService } from './ibrk-broker-adapter.service';
import { MetaApiBrokerAdapterService } from './meta-api-broker-adapter.service';
import { MetaApiService } from './meta-api.service';

/**
 * Factory service — returns the correct BrokerAdapter implementation
 * based on the `tipoBroker` field of the active OptimalWallet.
 *
 * Usage:
 *   const adapter: BrokerAdapter = this.brokerAdapterFactory.getAdapter(this.BrokoptimalWallet);
 *   adapter.getAccountSummary(wallet.metaApiAccountId ?? wallet.nombre).subscribe(...)
 */
@Injectable({
  providedIn: 'root'
})
export class BrokerAdapterFactory {

  constructor(
    private ibrkAdapter: IbrkBrokerAdapterService,
    private metaApiAdapter: MetaApiBrokerAdapterService,
    private metaApiService: MetaApiService
  ) {}

  getAdapter(wallet: OptimalWallet): BrokerAdapter {
    if (wallet?.tipoBroker === 'METAAPI') {
      return this.metaApiAdapter;
    }
    // Default: IBKR
    return this.ibrkAdapter;
  }

  /**
   * Convenience helper — resolves the account ID string to pass to adapter methods.
   * For IBKR this is the `nombre` (account name); for MetaAPI it is `metaApiAccountId`.
   */
  getAccountId(wallet: OptimalWallet): string {
    if (wallet?.tipoBroker === 'METAAPI') {
      return wallet.metaApiAccountId ?? '';
    }
    return wallet?.nombre ?? '';
  }

  /**
   * Before a flow that sends orders: false (with a warning toast) when the broker is MetaTrader on
   * the local gateway with "Algo Trading" off, so the flow stops before touching the broker or DB.
   */
  puedeOperar(tipoBroker: string | null | undefined): Observable<boolean> {
    return tipoBroker === 'METAAPI' ? this.metaApiService.verificarAlgoTradingParaOperar() : of(true);
  }
}
